import { memo, useMemo, useCallback, useEffect, useState, createRef } from 'react';
import { View, StyleSheet, Text, Image } from 'react-native';
import {
  I18n,
  Utils,
  Container,
  PhoneInput,
  FormFooter,
  Navigation,
  FormHelpers,
  Select,
  DateUtils,
} from '../../components';
import { THEME, __WEB__, COLOR, FONT } from '../../../constants/styles';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getCurrentEmployer } from '../../../selectors/entities';

import { PENDING, REQUESTING, SUCCESS, FAILED } from '../../../actions/statusTypes';
import { resetStatus, saveEmployerSetupData } from '../../employer/actions';

import moment from 'moment';

const pagerRef = createRef();
const landlineRef = createRef();

import Days from '../../components/schedule_cal/day_select';

function times(start) {
  const startMoment = moment().startOf('day');
  if (start) {
    startMoment.hours(parseInt(start.split(':')[0]));
    startMoment.minutes(parseInt(start.split(':')[1]));
    startMoment.add(15, 'minutes');
  }
  const retArray = [];
  let i;
  for (i = 0; i < 24 * 4; i++) {
    retArray.push(`${DateUtils.pad(startMoment.hours())}:${DateUtils.pad(startMoment.minutes())}`);
    startMoment.add(15, 'minutes');
  }
  return retArray;
}

function closingTimeOptions(start = '00:00') {
  const startHours = parseInt(start.split(':')[0]);
  const startMinutes = parseInt(start.split(':')[1]);
  return times(start).map((id) => {
    const currentHours = parseInt(id.split(':')[0]);
    const currentMinutes = parseInt(id.split(':')[1]);
    const nextDay =
      currentHours < startHours || (currentHours === startHours && currentMinutes <= startMinutes);
    return { label: `${id}${nextDay ? ' Next day' : ''}`, id };
  });
}

export default memo(
  connect(
    function (state, ownProps) {
      const employer = getCurrentEmployer(state);
      const clms = state.screens.employer.setupData?.clinicalLocationMedicalService?.find(
        (clms) =>
          clms.employer_clinical_location_id === ownProps.item.employer_clinical_location_id &&
          clms.employer_medical_service_id === ownProps.item.employer_medical_service_id
      );
      return {
        employer,
        saveSetupDataStatus: state.screens.employer.saveSetupDataStatus,
        errorMessage: state.screens.employer.errorMessage,
        successMessage: state.screens.employer.saveSetupDataStatus === SUCCESS ? 'Success!' : null,
        numOptions: [0, 1, 2, 3, 4, 5].map((id) => ({ label: id, id })),
        startTimeOptions: times().map((id) => ({ label: id, id })),
        clms,
      };
    },
    function (dispatch) {
      return {
        actions: bindActionCreators({ resetStatus, saveEmployerSetupData }, dispatch),
      };
    }
  )(function (props) {
    useEffect(function () {
      return function () {
        props.actions.resetStatus();
      };
    }, []);

    const schedule1 = props.clms?.shifts?.slice()?.sort(function (a, b) {
      if (a.days.join() > b.days.join()) return -1;
      else return 1;
    })?.[0];
    const schedule1SelectedDays =
      schedule1?.days?.reduce((result, current) => {
        result[current] = true;
        return result;
      }, {}) || {};

    const [pagerText, setPagerText] = useState(props.clms?.pager || '');
    const [landlineText, setLandlineText] = useState(props.clms?.landline || '');
    const [selectedDays, setSelectedDays] = useState(schedule1SelectedDays);
    const [num, setNum] = useState(schedule1?.hours?.length || 1);
    const [selectedSelect, setSelectedSelect] = useState(null);
    const [shifts, setShifts] = useState((schedule1?.hours || []).map((h) => ({ ...h })));

    function handleChangePagerText(text) {
      setPagerText(text);
    }
    function handleChangeLandlineText(text) {
      setLandlineText(text);
    }
    function handleSubmitEditingPager() {}
    function handleSubmitEditingLandline() {}

    function handleCancel() {
      Navigation.dismissAllModals();
    }

    function handleSave() {
      const fieldsToValidate = [pagerRef.current, landlineRef.current];
      const isValid = FormHelpers.validateFields(fieldsToValidate);
      if (!isValid) return;

      const days = Object.keys(selectedDays)
        .filter((day) => !!selectedDays[day])
        .map((d) => parseInt(d));

      const data = {
        ...props.clms,
        pager: pagerText?.trim() || null,
        landline: landlineText?.trim() || null,
        shifts: days.reduce((result, day) => {
          shifts.forEach((shift) => {
            result.push({
              ...shift,
              day,
            });
          });
          if (!shifts?.length) {
            result.push({ day });
          }
          return result;
        }, []),
      };
      delete data['employer_clinical_location_medical_service_id'];
      delete data['count'];
      props.actions.saveEmployerSetupData(
        props.employer.employerId,
        'clinicalLocationMedicalService',
        data
      );
    }

    function calcSelectedSchedule(days) {
      const selectedDaysStr = Object.keys(days)
        .filter((day) => !!days[day])
        .map((d) => parseInt(d))
        .sort()
        .join();
      const schedule =
        (selectedDaysStr &&
          props.clms?.shifts?.find((s) => s.days.join().indexOf(selectedDaysStr) >= 0)) ||
        null;
      if (schedule) {
        setNum(schedule?.hours?.length || 1);
        setShifts((schedule?.hours || []).map((h) => ({ ...h })));
      }
    }

    function handleDayPress(i) {
      const newSelectedDays = {
        ...selectedDays,
        [i]: !selectedDays?.[i],
      };
      setSelectedDays(newSelectedDays);
      calcSelectedSchedule(newSelectedDays);
    }

    const handleChangeNum = useCallback(({ id }) => {
      setNum(id);
      setShifts(shifts.slice(0, id - 1));
    });

    function renderSelect(selId, options, label, id, val, onChange) {
      function handleSelectFocus() {
        setSelectedSelect(selId);
      }
      return (
        <View style={selectedSelect === selId ? { zIndex: 1000 } : null}>
          <Select
            required={true}
            editable={true}
            closeOnSelect={true}
            onFocus={handleSelectFocus}
            options={options}
            label={label}
            value={{ label: val, id }}
            onValue={onChange}
            webMode={true}
            containerStyle={{ width: 200 }}
          />
        </View>
      );
    }

    function renderOpeningAndClosingTimes(item, i) {
      function handleChangeShift(type, val) {
        const newShifts = shifts.slice();
        for (let j = 0; j <= i; j++) {
          if (!newShifts[j]) newShifts.push({});
        }
        newShifts[i][type] = val;
        setShifts(newShifts);
      }
      function handleChangeOpenShift({ id }) {
        handleChangeShift('opening_time', id);
      }
      function handleChangeCloseShift({ id }) {
        handleChangeShift('ending_time', id);
      }
      const shiftNum = i + 1;
      const openingId = `opening_${shiftNum}`;
      const closingId = `closing_${shiftNum}`;
      const shift = shifts[i];
      const sectionSelected = [openingId, closingId].includes(selectedSelect);
      return (
        <View key={i} style={[THEME.basicRow, sectionSelected ? { zIndex: 1000 } : null]}>
          <View style={{ flex: 1 }}>
            {renderSelect(
              openingId,
              props.startTimeOptions,
              `Shift ${shiftNum} Opening Time`,
              shift?.opening_time,
              shift?.opening_time,
              handleChangeOpenShift
            )}
          </View>
          <View style={{ flex: 1 }}>
            {renderSelect(
              closingId,
              closingTimeOptions(shift?.opening_time),
              `Shift ${shiftNum} Closing Time`,
              shift?.ending_time,
              DateUtils.endTimeLabel(shift?.opening_time, shift?.ending_time),
              handleChangeCloseShift
            )}
          </View>
        </View>
      );
    }

    const footer = (
      <FormFooter
        onCancel={handleCancel}
        onSave={handleSave}
        cancelLabel="Cancel"
        saveLabel="Save"
        saveDisabled={!pagerText && !landlineText}
      />
    );

    return (
      <Container
        scrollView={true}
        stickyFooter={footer}
        contentContainerStyle={styles.containerContent}
        renderActivityOverlay={props.saveSetupDataStatus === REQUESTING}
        errorMessage={props.errorMessage?.message}
        successMessage={props.successMessage}
      >
        <View style={[styles.phoneNumberInputs, { paddingHorizontal: 25 }]}>
          <PhoneInput
            ref={pagerRef}
            value={pagerText}
            invalidText={I18n.t('form.phoneInvalid')}
            label={I18n.t('employer.deptPager')}
            placeholder="Pager"
            onChangeText={handleChangePagerText}
            onSubmitEditing={handleSubmitEditingPager}
            tipText={I18n.t('form.phoneTip')}
            containerStyle={{ flex: 1 }}
          />
          <View style={{ width: 30 }} />
          <PhoneInput
            ref={landlineRef}
            value={landlineText}
            invalidText={I18n.t('form.phoneInvalid')}
            label={I18n.t('employer.deptLandline')}
            onChangeText={handleChangeLandlineText}
            onSubmitEditing={handleSubmitEditingLandline}
            placeholder="Landline"
            tipText={I18n.t('form.phoneTip')}
            containerStyle={{ flex: 1 }}
          />
        </View>
        <Days onPressDay={handleDayPress} selectedDays={selectedDays} />

        <View style={[{ paddingHorizontal: 35, paddingVertical: 25, zIndex: 1000 }]}>
          {renderSelect('numSel', props.numOptions, 'Number of Shifts', num, num, handleChangeNum)}

          {Array(num).fill().map(renderOpeningAndClosingTimes)}
        </View>
      </Container>
    );
  })
);

const styles = StyleSheet.create({
  phoneNumberInputs: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    height: 120,
    zIndex: 1,
  },
  containerContent: {
    paddingTop: 25,
  },
});
